import { useAuth0 } from 'app/Auth'
import { useMutation, useQuery } from 'react-query'
import dayjs from 'lib/dayjs'

import {
  GetUsageReportsVariables,
  SurgeriesOutput,
  SurgeryDetailsOutput,
} from 'views/PatientReport/PatientReport.types'
import { SurgeryData } from 'lib/services/api/reports-service/preference-card/getProcedures/types'

const REPORTS_URL = process.env.REACT_APP_REPORTS_URL

const request = async (url: string, getAccessToken: () => Promise<string>) => {
  const token = await getAccessToken()
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error('Network response was not ok')
  }

  return response.json()
}

export const useListSurgeries = (queryParams?: GetUsageReportsVariables) => {
  const { getAccessToken } = useAuth0()

  return useQuery<SurgeriesOutput[]>({
    queryKey: ['surgeries', queryParams],
    queryFn: async () => {
      if (!REPORTS_URL) {
        throw new Error('API URL is not defined.')
      }

      const queryString = Object.entries(queryParams || {})
        .reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          }
          return acc
        }, [] as string[])
        .join('&')

      const url = `${REPORTS_URL}api/v1/reports/surgeries/?${queryString}`
      const data = await request(url, getAccessToken)
      return data.surgeries
    },
  })
}

export const useListSurgeriesWithPreferenceCards = (
  queryParams?: GetUsageReportsVariables & { enabled?: boolean }
) => {
  const { getAccessToken } = useAuth0()
  const { enabled = true, ...params } = queryParams || {}

  return useQuery<{ surgeries: SurgeryData[] }>({
    queryKey: ['surgeriesWithPreferenceCards', params],
    queryFn: async () => {
      if (!REPORTS_URL) {
        throw new Error('API URL is not defined.')
      }

      // Format dates as YYYY-MM-DD strings, ensuring we only get the date part
      const formattedParams = {
        ...params,
        startDate: dayjs(params.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(params.endDate).format('YYYY-MM-DD'),
      }

      const queryString = Object.entries(formattedParams)
        .reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            // Don't encode the date values since they're already in the correct format
            if (key === 'startDate' || key === 'endDate') {
              acc.push(`${key}=${value}`)
            } else {
              acc.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(
                  String(value)
                )}`
              )
            }
          }
          return acc
        }, [] as string[])
        .join('&')
      const url = `${REPORTS_URL}api/v1/reports/surgeries/with_preference_cards?${queryString}`
      const data = await request(url, getAccessToken)
      return data
    },
    enabled,
  })
}

export const useGetSurgeryDetails = (surgeryId: string) => {
  const { getAccessToken } = useAuth0()

  return useQuery<SurgeryDetailsOutput>({
    queryKey: ['surgeryDetails', surgeryId],
    queryFn: async () => {
      if (!REPORTS_URL) {
        throw new Error('API URL is not defined.')
      }
      if (!surgeryId) {
        throw new Error('Surgery ID is required')
      }

      const url = `${REPORTS_URL}api/v1/reports/surgeries/${surgeryId}`
      return request(url, getAccessToken)
    },
    enabled: !!surgeryId, // Only run the query if surgeryId is provided
  })
}

export const useGetSurgeryDetailsMutation = () => {
  const { getAccessToken } = useAuth0()

  return useMutation<SurgeryDetailsOutput, Error, string>({
    mutationFn: async (surgeryId: string) => {
      if (!REPORTS_URL) {
        throw new Error('API URL is not defined.')
      }
      if (!surgeryId) {
        throw new Error('Surgery ID is required')
      }

      const url = `${REPORTS_URL}api/v1/reports/surgeries/${surgeryId}`
      return request(url, getAccessToken)
    },
  })
}
